/**
 * Accommodation Service Module
 * 
 * This service provides a business logic layer between the UI components and Firebase Firestore.
 * It handles data validation, transformation, and business rules for accommodation management.
 * 
 * Key Responsibilities:
 * - CRUD operations for accommodation items
 * - Data validation and transformation
 * - Business logic implementation
 * - Type and status management
 * - Date range operations
 * - Day link management with rich metadata (Phase 2A)
 * - Accommodation-day relationship validation
 * - Context-aware linking (check-in, staying, check-out)
 * - Content block reference tracking (Phase 2B)
 * - Bidirectional content block relationship management
 * 
 * Data Structure:
 * accommodation {
 *   type: string,          // Type of accommodation (hotel, apartment, etc.)
 *   details: {             // Accommodation details
 *     name: string,        // Name of the accommodation
 *     city: string,        // City location
 *     checkInDate: string, // ISO date string
 *     checkOutDate: string // ISO date string
 *   },
 *   status: string,        // Current status (Planned, Booked, etc.)
 *   summary: string,       // Display title
 *   cost: {               // Cost information
 *     amount: number,
 *     currency: string,
 *     paid: boolean,
 *     dueDate: string
 *   },
 *   notes: string,
 *   dayLinks: [           // Enhanced day linking with metadata (Phase 2A)
 *     {
 *       dayId: string,    // Day ID reference
 *       date: string,     // ISO date string for the day
 *       context: string,  // 'check-in', 'staying', or 'check-out'
 *       linkedAt: string  // ISO timestamp when link was created
 *     }
 *   ],
 *   dayLinksLastValidated: string, // ISO timestamp of last validation
 *   contentBlocks: [      // Content block references (Phase 2B)
 *     {
 *       dayId: string,          // Day ID containing the content block
 *       contentBlockId: string, // ID of the specific content block
 *       date: string,           // ISO date string for the day
 *       context: string,        // 'check-in', 'staying', 'check-out', 'single-day'
 *       createdAt: string       // ISO timestamp when content block was created
 *     }
 *   ],
 *   contentBlocksLastUpdated: string, // ISO timestamp of last content block update
 *   createdAt: string,    // ISO date string
 *   updatedAt: string     // ISO date string
 * }
 */

import {
  getAccommodation,
  addAccommodation,
  updateAccommodation,
  deleteAccommodation,
  getAccommodationById,
  getAccommodationByDateRange,
  batchUpdateAccommodation,
  getDays,
  eventBridgeService,
  EVENT_TYPES,
  removeDocumentReferenceBlocks,
  updateDocumentReferenceBlocks,
  buildAccommodationPlaceUnlinkedPayload,
  linkRegistryConfigService,
  connectionRegistryService,
  shouldRouteThroughRegistry,
  LINK_TYPES
} from '@trip-planner-monorepo/shared';
import { FaHotel, FaHome, FaBuilding, FaUmbrellaBeach, FaBed } from 'react-icons/fa';
import { ACCOMMODATION_TYPES, ACCOMMODATION_STATUSES } from '../constants/accommodationTypes';
import { deleteField } from 'firebase/firestore';

export const accommodationService = {
  /**
   * Resolves a normalized, lowercase accommodation type identifier from various shapes
   * - Accepts string types (e.g., 'hotel')
   * - Accepts object types with an id property (e.g., { id: 'hotel', ... })
   * - Accepts fallback via accommodationType.id commonly present in form payloads
   */
  _resolveTypeId(raw) {
    if (!raw) return undefined;
    if (typeof raw === 'string') return raw.toLowerCase();
    if (typeof raw === 'object') {
      if (typeof raw.id === 'string') return raw.id.toLowerCase();
      if (raw.accommodationType && typeof raw.accommodationType.id === 'string') return raw.accommodationType.id.toLowerCase();
    }
    return undefined;
  },

  /**
   * Finds the ACCOMMODATION_TYPES config regardless of key casing or structure.
   * Supports:
   * - Keys like HOTEL / hotel
   * - Value objects with `.type` like 'HOTEL'
   */
  _getTypeConfig(rawType) {
    const normalized = this._resolveTypeId(rawType);
    if (!normalized) return undefined;
    // Direct key match (handles both TS lowercase keys and JS uppercase keys)
    const direct = ACCOMMODATION_TYPES[normalized] || ACCOMMODATION_TYPES[normalized.toUpperCase()];
    if (direct) return direct;
    // Fallback: scan values by their `type` field
    const values = Object.values(ACCOMMODATION_TYPES || {});
    return values.find(v => v && typeof v.type === 'string' && v.type.toLowerCase() === normalized);
  },
  /**
   * Fetches and sorts all accommodation items for a trip
   * Sorting priority:
   * 1. Check-in date (if available)
   * 2. Creation date (fallback)
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @returns {Promise<Array>} Sorted array of accommodation items
   * @throws {Error} If fetching fails
   */
  async fetchAccommodation(userId, tripId) {
    try {
      const accommodation = await getAccommodation(userId, tripId);
      return accommodation.sort((a, b) => {
        // First try sorting by details.checkInDate
        const dateA = a.details?.checkInDate;
        const dateB = b.details?.checkInDate;
        if (dateA && dateB) {
          return new Date(dateA) - new Date(dateB);
        }
        // Fall back to createdAt if checkInDate not available
        const createdAtA = a.createdAt || '';
        const createdAtB = b.createdAt || '';
        return createdAtA.localeCompare(createdAtB);
      });
    } catch (error) {
      console.error('Error fetching accommodation:', error);
      throw error;
    }
  },

  /**
   * Creates a new accommodation item with validation
   * Handles default values and data transformation
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {Object} accommodationData - The accommodation data to create
   * @returns {Promise<string>} ID of the created accommodation
   * @throws {Error} If validation fails or creation fails
   */
  async createAccommodation(userId, tripId, accommodationData) {
    try {
      console.log('Creating accommodation with data:', accommodationData);
      const validation = this.validateAccommodationData(accommodationData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join('\n'));
      }

      // Transform and set default values for new accommodation
      const newAccommodation = {
        type: this._resolveTypeId(accommodationData.type) || this._resolveTypeId(accommodationData) || accommodationData.type,
        details: accommodationData.details,
        status: accommodationData.status || 'Planned',
        summary: accommodationData.summary,
        cost: {
          amount: accommodationData.cost?.amount || 0,
          currency: accommodationData.cost?.currency || 'USD',
          paid: accommodationData.cost?.paid || false,
          dueDate: accommodationData.cost?.dueDate || null
        },
        notes: accommodationData.notes || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const accommodationId = await addAccommodation(userId, tripId, newAccommodation);

      // Fetch initial dayIds if any (typically added later, but include for completeness)
      const createdAccom = await this.getAccommodationById(userId, tripId, accommodationId);
      let dayIds = (createdAccom.contentBlocks || []).map(block => block.dayId).filter(Boolean);

      // If no dayIds from content blocks (common for new accommodations), resolve from date range
      if (dayIds.length === 0 && newAccommodation.details?.checkInDate && newAccommodation.details?.checkOutDate) {
        try {
          console.log(`AccommodationService: No content blocks found for new accommodation, resolving dayIds from date range`);
          const allDays = await getDays(userId, tripId);

          if (allDays && allDays.length > 0) {
            const checkInDate = newAccommodation.details.checkInDate;
            const checkOutDate = newAccommodation.details.checkOutDate;

            // Find days that fall within the accommodation date range (inclusive of check-in, exclusive of check-out)
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);

            const matchingDays = allDays.filter(day => {
              if (!day.date) return false;
              const dayDate = new Date(day.date);
              return dayDate >= checkIn && dayDate < checkOut;
            });

            dayIds = matchingDays.map(day => day.id);
            console.log(`AccommodationService: Resolved ${dayIds.length} dayIds from date range ${checkInDate} to ${checkOutDate} for new accommodation:`, dayIds);
          }
        } catch (error) {
          console.warn(`AccommodationService: Failed to resolve dayIds from date range for new accommodation:`, error);
          dayIds = []; // Fallback to empty array
        }
      }

      // Publish event for newly created accommodation
      // Enhanced: Include dayIds and accommodationId for place reference updates
      eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_ADDED, {
        id: accommodationId,
        tripId,
        userId,
        ...newAccommodation,
        dayIds,
        accommodationId
      });

      return accommodationId;
    } catch (error) {
      console.error('Error creating accommodation:', error);
      throw error;
    }
  },

  /**
   * Updates an existing accommodation item
   * Handles summary generation and data validation
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation to update
   * @param {Object} accommodationData - The updated accommodation data
   * @returns {Promise<Array>} Updated list of all accommodation items
   * @throws {Error} If validation fails or update fails
   */
  async updateAccommodation(userId, tripId, accommodationId, accommodationData) {
    try {
      const validation = this.validateAccommodationData(accommodationData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join('\n'));
      }

      // Fetch current accommodation before updating
      const currentAccommodation = await this.getAccommodationById(userId, tripId, accommodationId);
      const oldCity = currentAccommodation.details?.city;
      let oldPlaceReference = currentAccommodation.placeReference;
      const newCity = accommodationData.details?.city;

      console.log(`AccommodationService: City change analysis for accommodation ${accommodationId}:`);
      console.log(`  Old city: "${oldCity || 'null/empty'}"`);
      console.log(`  New city: "${newCity || 'null/empty'}"`);

      // Detect city changes: city value actually changed (regardless of new value being empty or not)
      const cityChanged = oldCity !== newCity; // Any change in city value
      const cityAdded = !oldCity && newCity; // City added to previously cityless accommodation  
      const cityRemoved = oldCity && !newCity; // City removed (cleared)
      const shouldTriggerCityEvent = cityChanged; // Trigger on any city change

      console.log(`  City changed: ${cityChanged}`);
      console.log(`  City added: ${cityAdded}`);
      console.log(`  City removed: ${cityRemoved}`);
      console.log(`  Should trigger city event: ${shouldTriggerCityEvent}`);

      // Registry-only hotels have no legacy placeReference, so oldPlaceReference above
      // is always empty — resolve the current place from the registry instead (Issue 5/15).
      // Without this, city-change/clear can never find the old place to retarget or drop.
      let locatedInLinkToArchive = null;
      if (shouldTriggerCityEvent) {
        try {
          const registryConfig = await linkRegistryConfigService?.getLinkRegistryConfig?.(userId, tripId);
          if (registryConfig && shouldRouteThroughRegistry(registryConfig)) {
            const links = await connectionRegistryService.getLinksForEntity(userId, tripId, 'accommodation', accommodationId);
            locatedInLinkToArchive = links.find(
              (link) => link.linkType === LINK_TYPES.LOCATED_IN && (link.linkLifecycle ?? 'active') === 'active'
            ) || null;
            if (locatedInLinkToArchive && !oldPlaceReference) {
              const placeEndpoint = locatedInLinkToArchive.entityLow?.type === 'place'
                ? locatedInLinkToArchive.entityLow
                : locatedInLinkToArchive.entityHigh;
              if (placeEndpoint?.id) {
                oldPlaceReference = { placeId: placeEndpoint.id };
              }
            }
          }
        } catch (error) {
          console.warn('accommodationService: could not resolve registry located_in for city change', error);
        }
      }

      const placeReferenceDeleted = accommodationData.placeReference && accommodationData.placeReference._methodName === 'deleteField' && oldPlaceReference;

      const updateData = {
        ...accommodationData,
        type: this._resolveTypeId(accommodationData.type) || this._resolveTypeId(accommodationData) || accommodationData.type,
        updatedAt: new Date().toISOString()
      };

      if (shouldTriggerCityEvent) {
        // Remove the old place reference if city changed or was added
        updateData.placeReference = deleteField();
        console.log(`  Removing old place reference due to city change/addition`);
      }

      // Intelligent summary handling
      if (!updateData.summary) {
        const normalizedType = this._resolveTypeId(updateData.type) || this._resolveTypeId(updateData);
        const typeConfig = this._getTypeConfig(normalizedType);
        if (typeConfig && typeConfig.createSummary) {
          updateData.summary = typeConfig.createSummary(updateData.details);
        }
      }

      await updateAccommodation(userId, tripId, accommodationId, updateData);

      if (locatedInLinkToArchive) {
        try {
          await connectionRegistryService.removeLink(
            userId,
            tripId,
            locatedInLinkToArchive.linkId || locatedInLinkToArchive.id
          );
        } catch (error) {
          console.warn('accommodationService: failed to archive located_in link on city change', error);
        }
      }

      // After update, fetch affected dayIds from contentBlocks for event payload
      const updatedAccom = await this.getAccommodationById(userId, tripId, accommodationId);
      let dayIds = (updatedAccom.contentBlocks || []).map(block => block.dayId).filter(Boolean);

      // If no dayIds from content blocks, resolve from date range
      if (dayIds.length === 0 && updatedAccom.details?.checkInDate && updatedAccom.details?.checkOutDate) {
        try {
          console.log(`AccommodationService: No content blocks found for updated accommodation, resolving dayIds from date range`);
          const allDays = await getDays(userId, tripId);

          if (allDays && allDays.length > 0) {
            const checkInDate = updatedAccom.details.checkInDate;
            const checkOutDate = updatedAccom.details.checkOutDate;

            // Find days that fall within the accommodation date range (inclusive of check-in, exclusive of check-out)
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);

            const matchingDays = allDays.filter(day => {
              if (!day.date) return false;
              const dayDate = new Date(day.date);
              return dayDate >= checkIn && dayDate < checkOut;
            });

            dayIds = matchingDays.map(day => day.id);
            console.log(`AccommodationService: Resolved ${dayIds.length} dayIds from date range ${checkInDate} to ${checkOutDate} for updated accommodation:`, dayIds);
          }
        } catch (error) {
          console.warn(`AccommodationService: Failed to resolve dayIds from date range for updated accommodation:`, error);
          dayIds = []; // Fallback to empty array
        }
      }

      if (shouldTriggerCityEvent) {
        // Enhanced: Add dayIds and accommodationId to payload for place updates
        eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_CITY_CHANGED, {
          id: accommodationId,
          tripId,
          userId,
          type: updateData.type,
          details: updateData.details,
          status: updateData.status,
          summary: updateData.summary,
          cost: updateData.cost,
          updatedAt: updateData.updatedAt,
          oldCity,
          newCity,
          oldPlaceReference,
          dayIds,
          accommodationId
        });

        if (oldPlaceReference) {
          eventBridgeService.publish(
            EVENT_TYPES.ACCOMMODATION_PLACE_UNLINKED,
            buildAccommodationPlaceUnlinkedPayload({
              userId,
              tripId,
              accommodationId,
              oldPlaceReference,
              reason: 'city_changed'
            })
          );
        }
      } else {
        // Enhanced: Add dayIds and accommodationId for general updates
        eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_UPDATED, {
          id: accommodationId,
          tripId,
          userId,
          dayIds,
          accommodationId
        });
      }

      // Publish ACCOMMODATION_PLACE_UNLINKED event if place reference was deleted
      if (placeReferenceDeleted) {
        eventBridgeService.publish(
          EVENT_TYPES.ACCOMMODATION_PLACE_UNLINKED,
          buildAccommodationPlaceUnlinkedPayload({
            userId,
            tripId,
            accommodationId,
            oldPlaceReference,
            reason: 'place_reference_deleted'
          })
        );
      }

      // Always publish ACCOMMODATION_UPDATED event for Phase 2B date change detection
      console.log(`  Publishing ACCOMMODATION_UPDATED event for accommodation ${accommodationId}`);
      eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_UPDATED, {
        id: accommodationId,
        tripId,
        userId,
        type: updateData.type,
        details: updateData.details,
        status: updateData.status,
        summary: updateData.summary,
        cost: updateData.cost,
        updatedAt: updateData.updatedAt,
        oldAccommodation: currentAccommodation, // Include the old accommodation data for comparison
        newAccommodation: updateData // Include the new accommodation data
      });

      return { updated: await this.fetchAccommodation(userId, tripId, false), cityChanged, oldCity, oldPlaceReference };
    } catch (error) {
      console.error('Error updating accommodation:', error);
      throw error;
    }
  },

  /**
   * Deletes an accommodation item and publishes deletion event for place cleanup analysis
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation to delete
   * @returns {Promise<Array>} Updated list of all accommodation items
   * @throws {Error} If deletion fails
   */
  async deleteAccommodation(userId, tripId, accommodationId) {
    try {
      // Fetch accommodation details before deletion for event payload
      const accommodationToDelete = await getAccommodationById(userId, tripId, accommodationId);

      console.log(`AccommodationService: Deleting accommodation ${accommodationId}:`, {
        name: accommodationToDelete.details?.name || 'Unnamed',
        city: accommodationToDelete.details?.city || 'No city',
        hasPlaceReference: !!accommodationToDelete.placeReference?.placeId
      });

      // Perform the actual deletion
      await deleteAccommodation(userId, tripId, accommodationId);

      // Publish ACCOMMODATION_DELETED event with pre-deletion details for place cleanup analysis
      eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_DELETED, {
        id: accommodationId,
        accommodationId,
        tripId,
        userId,
        summary: accommodationToDelete.summary,
        details: accommodationToDelete.details,
        placeReference: accommodationToDelete.placeReference || null, // Include place reference for cleanup analysis
        oldCity: accommodationToDelete.details?.city || null, // Include old city for context
        type: accommodationToDelete.type,
        status: accommodationToDelete.status,
        deletedAt: new Date().toISOString(),
        timestamp: new Date().toISOString()
      });

      console.log(`AccommodationService: Published ACCOMMODATION_DELETED event for accommodation ${accommodationId}`);

      return this.fetchAccommodation(userId, tripId, false);
    } catch (error) {
      console.error('Error deleting accommodation:', error);
      throw error;
    }
  },

  /**
   * Validates accommodation data against business rules
   * Checks:
   * - Required fields based on type
   * - Title information presence
   * - Date logic (check-out after check-in)
   * - Type validity
   * 
   * @param {Object} accommodationData - The accommodation data to validate
   * @returns {Object} Validation result {isValid: boolean, errors: string[]}
   */
  validateAccommodationData(accommodationData) {
    const errors = [];

    // Validate basic structure
    if (!accommodationData.type) {
      errors.push('Accommodation type is required');
      return { isValid: false, errors };
    }

    // Check if we have enough info to create a title
    if (!accommodationData.details) {
      errors.push('Accommodation details are required');
      return { isValid: false, errors };
    }

    const hasTitleInfo = Boolean(
      accommodationData.details.name?.trim() ||
      accommodationData.details.city?.trim()
    );

    if (!hasTitleInfo) {
      errors.push('Please provide at least accommodation name or city');
    }

    // Validate required fields based on accommodation type
    const resolvedType = this._resolveTypeId(accommodationData.type)
      || this._resolveTypeId(accommodationData)
      || (typeof accommodationData.type === 'string' ? accommodationData.type.toLowerCase() : undefined);
    const typeConfig = this._getTypeConfig(resolvedType);
    if (!typeConfig) {
      console.error('Validation error: Unknown accommodation type', {
        incomingType: accommodationData.type,
        resolvedType,
        availableTypes: Object.keys(ACCOMMODATION_TYPES || {})
      });
    }
    if (!typeConfig) {
      errors.push('Invalid accommodation type');
      return { isValid: false, errors };
    }

    // Validate type-specific required fields (only if config provides fields)
    const typeFields = (typeConfig && typeof typeConfig.fields === 'object') ? typeConfig.fields : {};
    Object.entries(typeFields).forEach(([fieldName, fieldConfig]) => {
      // If the field is 'city' and a hotel name is already provided, skip this required check for city.
      if (fieldName === 'city' && accommodationData.details?.name?.trim()) {
        return; // Skip requiring city if name is present
      }
      if (fieldConfig.required && !accommodationData.details?.[fieldName]) {
        errors.push(`${fieldConfig.label} is required`);
      }
    });

    // Validate date logic
    if (accommodationData.details && accommodationData.details.checkInDate && accommodationData.details.checkOutDate) {
      const checkIn = new Date(accommodationData.details.checkInDate);
      const checkOut = new Date(accommodationData.details.checkOutDate);
      if (checkOut < checkIn) {
        errors.push('Check-out date cannot be before check-in date');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Core Field Validation for Accommodation Forms
   * 
   * Validates that accommodation has at least one core field populated.
   * Core fields: accommodation name OR city (not both empty).
   * 
   * This function is context-agnostic and works for both:
   * - New accommodation creation (both fields start empty)
   * - Existing accommodation editing (user might clear existing data)
   * 
   * Edge cases handled:
   * - null/undefined values
   * - Empty strings
   * - Whitespace-only strings
   * - Missing details object
   * 
   * @param {Object} accommodationData - Accommodation data object
   * @param {Object} [accommodationData.details] - Nested details object
   * @param {string} [accommodationData.details.name] - Accommodation name
   * @param {string} [accommodationData.details.city] - City name
   * @returns {boolean} True if at least one core field has content, false otherwise
   * 
   * @example
   * // Valid cases
   * hasRequiredCoreFields({ details: { name: "Hotel ABC", city: "" } }); // true
   * hasRequiredCoreFields({ details: { name: "", city: "Paris" } }); // true
   * hasRequiredCoreFields({ details: { name: "Hotel", city: "Paris" } }); // true
   * 
   * // Invalid cases
   * hasRequiredCoreFields({ details: { name: "", city: "" } }); // false
   * hasRequiredCoreFields({ details: { name: "   ", city: "  " } }); // false
   * hasRequiredCoreFields({ details: {} }); // false
   * hasRequiredCoreFields({}); // false
   */
  hasRequiredCoreFields(accommodationData) {
    // Safely extract and trim core field values
    const name = accommodationData?.details?.name?.trim() || '';
    const city = accommodationData?.details?.city?.trim() || '';

    // At least one core field must have content
    return name.length > 0 || city.length > 0;
  },

  /**
   * Returns list of available accommodation types with their configurations
   * @returns {Array<Object>} Array of accommodation type configurations
   */
  getAccommodationTypes() {
    return Object.entries(ACCOMMODATION_TYPES).map(([key, config]) => ({
      id: key,
      ...config
    }));
  },

  /**
   * Returns list of possible accommodation statuses
   * @returns {Array<string>} Array of status options
   */
  getAccommodationStatuses() {
    return ACCOMMODATION_STATUSES;
  },

  /**
   * Returns the appropriate icon component for an accommodation type
   * @param {string} type - The type of accommodation
   * @returns {ReactComponent} Icon component from react-icons/fa
   */
  getAccommodationIcon(type) {
    const accommodationType = ACCOMMODATION_TYPES[type];
    if (accommodationType) {
      return accommodationType.icon;
    }
    return FaHotel; // Default icon
  },

  /**
   * Retrieves a single accommodation item by ID
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation to retrieve
   * @returns {Promise<Object>} The accommodation item
   * @throws {Error} If retrieval fails
   */
  async getAccommodationById(userId, tripId, accommodationId) {
    try {
      const accommodation = await getAccommodationById(userId, tripId, accommodationId);

      // Populate contentBlocks metadata for this accommodation
      try {
        const contentBlocksData = await this.getContentBlocks(userId, tripId, accommodationId);
        return {
          ...accommodation,
          contentBlocks: contentBlocksData.contentBlocks || []
        };
      } catch (error) {
        console.warn(`Failed to get content blocks for accommodation ${accommodationId}:`, error);
        return {
          ...accommodation,
          contentBlocks: []
        };
      }
    } catch (error) {
      console.error('Error fetching accommodation by ID:', error);
      throw error;
    }
  },

  /**
   * Retrieves accommodations that overlap with a given date range
   * Useful for:
   * - Finding available dates
   * - Checking for booking conflicts
   * - Viewing accommodations for a specific period
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} startDate - Start date in ISO format
   * @param {string} endDate - End date in ISO format
   * @returns {Promise<Array>} Array of overlapping accommodation items
   * @throws {Error} If retrieval fails
   */
  async getAccommodationByDateRange(userId, tripId, startDate, endDate) {
    try {
      return await getAccommodationByDateRange(userId, tripId, startDate, endDate);
    } catch (error) {
      console.error('Error fetching accommodation by date range:', error);
      throw error;
    }
  },

  /**
   * Performs batch updates on multiple accommodation items
   * Useful for:
   * - Mass status updates
   * - Date adjustments across multiple bookings
   * - Bulk property updates
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {Array<Object>} updates - Array of update objects {id, ...data}
   * @returns {Promise<boolean>} Success indicator
   * @throws {Error} If batch update fails
   */
  async batchUpdateAccommodation(userId, tripId, updates) {
    try {
      return await batchUpdateAccommodation(userId, tripId, updates);
    } catch (error) {
      console.error('Error performing batch update:', error);
      throw error;
    }
  },

  /**
   * Initializes the accommodation service
   * Subscribes to relevant events
   */
  init() {
    // Subscribe to suggestion created events
    eventBridgeService.subscribe(EVENT_TYPES.SUGGESTION_CREATED, this.handleSuggestionCreated.bind(this));
    console.log(`AccommodationService subscribed to ${EVENT_TYPES.SUGGESTION_CREATED} event.`);

    // Subscribe to link accommodation to place events
    eventBridgeService.subscribe(EVENT_TYPES.LINK_ACCOMMODATION_TO_PLACE, this.handleLinkAccommodationToPlace.bind(this));
    console.log(`AccommodationService subscribed to ${EVENT_TYPES.LINK_ACCOMMODATION_TO_PLACE} event.`);

    // Subscribe to document updated events
    eventBridgeService.subscribe(EVENT_TYPES.DOCUMENT_UPDATED, this.handleDocumentUpdated.bind(this));
    console.log(`AccommodationService subscribed to ${EVENT_TYPES.DOCUMENT_UPDATED} event.`);

    // Subscribe to document deleted events
    eventBridgeService.subscribe(EVENT_TYPES.DOCUMENT_DELETED, this.handleDocumentDeleted.bind(this));
    console.log(`AccommodationService subscribed to ${EVENT_TYPES.DOCUMENT_DELETED} event.`);

    console.log('Accommodation service initialized and all relevant event subscriptions are active.');
  },

  /**
   * Handles suggestion created events
   * Specifically handles updating accommodation with place references
   * 
   * @param {Object} suggestionData - The suggestion data
   */
  async handleSuggestionCreated(suggestionData) {
    try {
      if (suggestionData.type === 'UPDATE_ACCOMMODATION_PLACE_REFERENCE') {
        await this.updateAccommodationPlaceReference(
          suggestionData.userId,
          suggestionData.tripId,
          suggestionData.accommodationId,
          suggestionData.placeId
        );
      }
    } catch (error) {
      console.error('Error handling suggestion:', error);
    }
  },

  /**
   * Updates an accommodation with a reference to a place
   * Links accommodation to place by ID only - place ID is the source of truth for display
   * Does not modify accommodation city text - display fetches place data as needed
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation to update
   * @param {string} placeId - The ID of the place to link to
   * @returns {Promise<void>} 
   */
  async updateAccommodationPlaceReference(userId, tripId, accommodationId, placeId) {
    try {
      // Suppress the legacy placeReference write only on registry_only trips;
      // legacy_only and dual_write must still write it. Defensive: default to
      // writing legacy if the registry config can't be read (unmigrated/test).
      let config = null;
      let suppressLegacy = false;
      try {
        config = await linkRegistryConfigService?.getLinkRegistryConfig?.(userId, tripId);
        suppressLegacy = config?.writeMode === 'registry_only';
      } catch (error) {
        console.warn('accommodationService: could not read link registry config; defaulting to legacy write', error);
      }

      // Registry write happens regardless of legacy suppression — this is the fix for
      // the place-suggestion/agent/event-handler paths that previously relied solely
      // on the legacy `placeReference` write below, which is a no-op on registry_only.
      if (config && shouldRouteThroughRegistry(config)) {
        try {
          await connectionRegistryService.createLink(
            userId,
            tripId,
            { type: 'accommodation', id: accommodationId },
            { type: 'place', id: placeId },
            {
              linkType: LINK_TYPES.LOCATED_IN,
              metadata: { createdBy: 'accommodation_place_reference' },
              skipLegacyWrite: config.writeMode === 'registry_only'
            }
          );
        } catch (registryError) {
          console.error(
            `accommodationService: failed to create located_in registry link for accommodation ${accommodationId} -> place ${placeId}`,
            registryError
          );
          if (suppressLegacy) {
            throw registryError;
          }
        }
      }

      if (suppressLegacy) {
        console.log(
          `Skipping legacy placeReference write for accommodation ${accommodationId} (registry_only write mode)`
        );
      }

      console.log(`Updating accommodation ${accommodationId} with place reference ${placeId}`);

      // Get current accommodation data — needed below for the event payload even
      // when the legacy placeReference write itself is suppressed.
      const accommodation = await this.getAccommodationById(userId, tripId, accommodationId);

      if (!accommodation) {
        throw new Error(`Accommodation ${accommodationId} not found`);
      }

      if (!suppressLegacy) {
        // Update the accommodation with the place reference
        const updateData = {
          ...accommodation,
          placeReference: {
            placeId,
            linkedAt: new Date().toISOString()
          },
          updatedAt: new Date().toISOString()
        };

        await updateAccommodation(userId, tripId, accommodationId, updateData);
        console.log(`Successfully linked accommodation to place ${placeId}`);
      }

      // Fetch dayIds from contentBlocks for event payload, or resolve from date range if no content blocks exist yet
      let dayIds = (accommodation.contentBlocks || []).map(block => block.dayId).filter(Boolean);

      // If no dayIds from content blocks (common for new accommodations), resolve from date range
      if (dayIds.length === 0 && accommodation.details?.checkInDate && accommodation.details?.checkOutDate) {
        try {
          console.log(`AccommodationService: No content blocks found, resolving dayIds from date range for accommodation ${accommodationId}`);
          const allDays = await getDays(userId, tripId);

          if (allDays && allDays.length > 0) {
            const checkInDate = accommodation.details.checkInDate;
            const checkOutDate = accommodation.details.checkOutDate;

            // Find days that fall within the accommodation date range (inclusive of check-in, exclusive of check-out)
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);

            const matchingDays = allDays.filter(day => {
              if (!day.date) return false;
              const dayDate = new Date(day.date);
              return dayDate >= checkIn && dayDate < checkOut;
            });

            dayIds = matchingDays.map(day => day.id);
            console.log(`AccommodationService: Resolved ${dayIds.length} dayIds from date range ${checkInDate} to ${checkOutDate}:`, dayIds);
          }
        } catch (error) {
          console.warn(`AccommodationService: Failed to resolve dayIds from date range for accommodation ${accommodationId}:`, error);
          dayIds = []; // Fallback to empty array
        }
      }

      // Publish accommodation place linked event
      // Enhanced: Add dayIds and accommodationId for place reference updates
      eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_PLACE_LINKED, {
        userId,
        tripId,
        accommodationId,
        placeId,
        timestamp: new Date().toISOString(),
        dayIds,
        accommodationName: accommodation.details?.name || accommodation.summary || 'Accommodation'
      });
      console.log(`AccommodationService: Published ACCOMMODATION_PLACE_LINKED event for accommodation ${accommodationId}`);

    } catch (error) {
      console.error('Error updating accommodation with place reference:', error);
      throw error;
    }
  },

  // Handler for LINK_ACCOMMODATION_TO_PLACE event
  async handleLinkAccommodationToPlace(eventPayload) {
    const { accommodationId, placeId, userId, tripId } = eventPayload;
    console.log(`AccommodationService: Received ${EVENT_TYPES.LINK_ACCOMMODATION_TO_PLACE} event for accommodation ${accommodationId} to link with place ${placeId}.`);

    try {
      if (!userId || !tripId || !accommodationId || !placeId) {
        console.error('AccommodationService: Invalid payload for LINK_ACCOMMODATION_TO_PLACE. Missing one or more required IDs.', eventPayload);
        return;
      }

      // Link accommodation to place by ID only - no city text synchronization
      // Place ID is the single source of truth for display purposes
      await this.updateAccommodationPlaceReference(userId, tripId, accommodationId, placeId);

      console.log(`AccommodationService: Successfully processed LINK_ACCOMMODATION_TO_PLACE for accommodation ${accommodationId} and place ${placeId}.`);
    } catch (error) {
      console.error(`AccommodationService: Error processing LINK_ACCOMMODATION_TO_PLACE for accommodation ${accommodationId}:`, error, eventPayload);
      // Optionally, publish a failure event or implement retry logic
    }
  },

  /**
   * Retrieves all accommodations linked to a specific place
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} placeId - The ID of the place to find accommodations for
   * @returns {Promise<Array>} Array of accommodation items linked to the place
   * @throws {Error} If retrieval fails
   */
  async getAccommodationsByPlace(userId, tripId, placeId) {
    try {
      console.log(`Fetching accommodations for place ${placeId}`);
      const allAccommodations = await this.fetchAccommodation(userId, tripId, false);

      // Registry-resolved located_in ids — needed on registry_only trips, where
      // legacy placeReference is absent (Issue 6).
      let registryLinkedIds = new Set();
      try {
        const links = await connectionRegistryService.getLinksForEntity(userId, tripId, 'place', placeId);
        registryLinkedIds = new Set(
          links
            .filter((link) => link.linkType === LINK_TYPES.LOCATED_IN && (link.linkLifecycle ?? 'active') === 'active')
            .map((link) => (link.entityLow?.type === 'accommodation' ? link.entityLow.id : link.entityHigh?.id))
            .filter(Boolean)
        );
      } catch (error) {
        console.warn('accommodationService: could not read registry links for getAccommodationsByPlace', error);
      }

      // Filter accommodations that reference this place, via legacy field or registry link
      const linkedAccommodations = allAccommodations.filter(accommodation =>
        accommodation.placeReference?.placeId === placeId || registryLinkedIds.has(accommodation.id)
      );

      console.log(`Found ${linkedAccommodations.length} accommodations linked to place ${placeId}`);
      return linkedAccommodations;

    } catch (error) {
      console.error('Error fetching accommodations by place:', error);
      throw error;
    }
  },

  /**
   * Removes the place reference from an accommodation
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation to unlink
   * @returns {Promise<Object>} The updated accommodation
   * @throws {Error} If unlinking fails
   */
  async unlinkAccommodationFromPlace(userId, tripId, accommodationId) {
    try {
      console.log(`Unlinking accommodation ${accommodationId} from its place reference`);

      // Get current accommodation data
      const accommodation = await this.getAccommodationById(userId, tripId, accommodationId);

      if (!accommodation) {
        throw new Error(`Accommodation ${accommodationId} not found`);
      }

      // Store the old place reference for event publishing
      const oldPlaceReference = accommodation.placeReference;

      // Remove the place reference using deleteField()
      const updateData = {
        ...accommodation,
        placeReference: deleteField(),
        updatedAt: new Date().toISOString()
      };

      await updateAccommodation(userId, tripId, accommodationId, updateData);

      // If there was a previous place reference, publish an event
      if (oldPlaceReference) {
        eventBridgeService.publish(
          EVENT_TYPES.ACCOMMODATION_PLACE_UNLINKED,
          buildAccommodationPlaceUnlinkedPayload({
            userId,
            tripId,
            accommodationId,
            oldPlaceReference,
            reason: 'manual_unlink'
          })
        );
      }

      console.log(`Successfully unlinked accommodation ${accommodationId} from place`);
      return updateData;

    } catch (error) {
      console.error('Error unlinking accommodation from place:', error);
      throw error;
    }
  },

  // =====================================================
  // DAY MANAGEMENT METHODS - Phase 2A
  // =====================================================

  /**
   * Gets all day references for a specific accommodation with rich metadata
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation
   * @returns {Promise<Object>} Object containing dayLinks array and metadata
   * @throws {Error} If retrieval fails
   */
  async getDayReferencesForAccommodation(userId, tripId, accommodationId) {
    try {
      console.log(`Fetching day references for accommodation ${accommodationId}`);

      // Get the accommodation details first to ensure it exists
      const accommodation = await this.getAccommodationById(userId, tripId, accommodationId);
      if (!accommodation) {
        throw new Error(`Accommodation ${accommodationId} not found`);
      }

      // Process dayLinks with backward compatibility
      const rawDayLinks = accommodation.dayLinks || [];
      const enrichedDayLinks = this._processLegacyDayLinks(rawDayLinks);

      // Create summary metadata
      const contextSummary = enrichedDayLinks.reduce((acc, link) => {
        acc[link.context] = (acc[link.context] || 0) + 1;
        return acc;
      }, {});

      const result = {
        dayLinks: enrichedDayLinks,
        totalLinks: enrichedDayLinks.length,
        contextSummary,
        accommodationDateRange: {
          checkInDate: accommodation.details?.checkInDate,
          checkOutDate: accommodation.details?.checkOutDate
        },
        lastValidated: accommodation.dayLinksLastValidated || null
      };

      console.log(`Found ${enrichedDayLinks.length} day references for accommodation ${accommodationId}:`, contextSummary);
      return result;

    } catch (error) {
      console.error('Error fetching day references for accommodation:', error);
      throw error;
    }
  },

  /**
   * Updates the day links for a specific accommodation with rich metadata
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation
   * @param {Array} dayLinks - Array of day link objects with metadata: [{dayId, date, context, linkedAt}] or legacy array of dayIds
   * @returns {Promise<Object>} Updated accommodation object
   * @throws {Error} If update fails
   */
  async updateAccommodationDayLinks(userId, tripId, accommodationId, dayLinks) {
    try {
      // dayLinks is a legacy field the extractor reads to rebuild stays_on links
      // (N5) — writing a partial/stale array here on registry_only trips is a latent
      // source of wrong links if extraction is ever re-run. The registry is the
      // source of truth there, so skip this write entirely.
      let skipLegacyWrite = false;
      try {
        const config = await linkRegistryConfigService?.getLinkRegistryConfig?.(userId, tripId);
        skipLegacyWrite = config?.writeMode === 'registry_only';
      } catch (error) {
        console.warn('accommodationService: could not read link registry config; defaulting to legacy write', error);
      }
      if (skipLegacyWrite) {
        console.log(`Skipping legacy dayLinks write for accommodation ${accommodationId} (registry_only write mode)`);
        return null;
      }

      console.log(`Updating day links for accommodation ${accommodationId}:`, dayLinks);

      // Get current accommodation data
      const accommodation = await this.getAccommodationById(userId, tripId, accommodationId);
      if (!accommodation) {
        throw new Error(`Accommodation ${accommodationId} not found`);
      }

      // Process and enrich day links (handle both legacy array format and new object format)
      const enrichedDayLinks = this._enrichDayLinks(dayLinks, accommodation);

      // Update the accommodation with new day links
      const updateData = {
        ...accommodation,
        dayLinks: enrichedDayLinks,
        dayLinksLastValidated: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await updateAccommodation(userId, tripId, accommodationId, updateData);

      // Create context summary for event
      const contextSummary = enrichedDayLinks.reduce((acc, link) => {
        acc[link.context] = (acc[link.context] || 0) + 1;
        return acc;
      }, {});

      // Extract date range from enriched links
      const dates = enrichedDayLinks.map(link => link.date).filter(date => date).sort();
      const dateRange = dates.length > 0 ? {
        startDate: dates[0],
        endDate: dates[dates.length - 1]
      } : null;

      // Publish enhanced event for day link update
      eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_DAY_LINK_CREATED, {
        userId,
        tripId,
        accommodationId,
        dayLinks: enrichedDayLinks,
        contextSummary,
        dateRange,
        totalLinks: enrichedDayLinks.length,
        timestamp: updateData.updatedAt
      });

      console.log(`Successfully updated ${enrichedDayLinks.length} day links for accommodation ${accommodationId}:`, contextSummary);
      return updateData;

    } catch (error) {
      console.error('Error updating accommodation day links:', error);
      throw error;
    }
  },

  /**
   * Gets accommodation details formatted for day content display
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation
   * @param {Object} options - Formatting options
   * @returns {Promise<string>} Formatted accommodation content for day entry
   * @throws {Error} If formatting fails
   */
  async getAccommodationContentForDay(userId, tripId, accommodationId, options = {}) {
    try {
      const accommodation = await this.getAccommodationById(userId, tripId, accommodationId);
      if (!accommodation) {
        throw new Error(`Accommodation ${accommodationId} not found`);
      }

      const name = accommodation.details?.name || accommodation.summary || 'Accommodation';
      const city = accommodation.details?.city || 'Unknown location';

      // Format according to standardized pattern: "Stay at [Name]: [City]"
      let content = `Stay at ${name}: ${city}`;

      // Add context indicators based on options
      if (options.context === 'check-in') {
        content += ' (check-in)';
      } else if (options.context === 'check-out') {
        content += ' (Check-out)';
      }

      return content;

    } catch (error) {
      console.error('Error getting accommodation content for day:', error);
      throw error;
    }
  },

  /**
   * Validates day links against current accommodation date ranges
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation
   * @returns {Promise<Object>} Validation results with repair suggestions
   * @throws {Error} If validation fails
   */
  async validateAccommodationDayLinks(userId, tripId, accommodationId) {
    try {
      console.log(`Validating day links for accommodation ${accommodationId}`);

      // Get accommodation details
      const accommodation = await this.getAccommodationById(userId, tripId, accommodationId);
      if (!accommodation) {
        throw new Error(`Accommodation ${accommodationId} not found`);
      }

      const checkInDate = accommodation.details?.checkInDate;
      const checkOutDate = accommodation.details?.checkOutDate;
      const dayLinks = accommodation.dayLinks || [];

      // Process legacy format if needed
      const enrichedDayLinks = this._processLegacyDayLinks(dayLinks);

      const validationResult = {
        accommodationId,
        hasDateRange: !!(checkInDate && checkOutDate),
        totalLinks: enrichedDayLinks.length,
        validLinks: [],
        invalidLinks: [],
        missingDates: [],
        dateRangeMismatch: false,
        contextErrors: [],
        repairSuggestions: []
      };

      // If no date range, only validate basic link structure
      if (!checkInDate) {
        enrichedDayLinks.forEach(link => {
          if (link.dayId && typeof link.dayId === 'string') {
            validationResult.validLinks.push(link);
          } else {
            validationResult.invalidLinks.push({
              link,
              reason: 'Invalid dayId'
            });
          }
        });

        return validationResult;
      }

      // Parse accommodation date range
      const checkIn = new Date(checkInDate);
      const checkOut = checkOutDate ? new Date(checkOutDate) : null;

      // Validate each link against date range
      enrichedDayLinks.forEach(link => {
        const linkValidation = { link, issues: [] };

        // Check basic structure
        if (!link.dayId || typeof link.dayId !== 'string') {
          linkValidation.issues.push('Invalid dayId');
        }

        // Check date consistency if available
        if (link.date) {
          const linkDate = new Date(link.date);

          if (isNaN(linkDate.getTime())) {
            linkValidation.issues.push('Invalid date format');
          } else {
            // Check if date is within accommodation range
            const withinRange = linkDate >= checkIn &&
              (!checkOut || linkDate < checkOut);

            if (!withinRange) {
              linkValidation.issues.push('Date outside accommodation range');
              validationResult.dateRangeMismatch = true;
            }
          }
        } else {
          validationResult.missingDates.push(link.dayId);
        }

        // Validate context appropriateness
        if (link.context && link.date) {
          const linkDate = new Date(link.date);

          if (link.context === 'check-in' && linkDate.toISOString().split('T')[0] !== checkInDate) {
            linkValidation.issues.push('Check-in context mismatch');
          }

          if (link.context === 'check-out' && checkOut) {
            const dayBeforeCheckOut = new Date(checkOut);
            dayBeforeCheckOut.setDate(dayBeforeCheckOut.getDate() - 1);

            if (linkDate.toISOString().split('T')[0] !== dayBeforeCheckOut.toISOString().split('T')[0]) {
              linkValidation.issues.push('Check-out context mismatch');
            }
          }
        }

        if (linkValidation.issues.length === 0) {
          validationResult.validLinks.push(link);
        } else {
          validationResult.invalidLinks.push(linkValidation);
        }
      });

      // Generate repair suggestions
      if (validationResult.invalidLinks.length > 0) {
        validationResult.repairSuggestions.push('Remove invalid day links');
      }

      if (validationResult.missingDates.length > 0) {
        validationResult.repairSuggestions.push('Populate missing dates from day records');
      }

      if (validationResult.dateRangeMismatch) {
        validationResult.repairSuggestions.push('Update day links to match accommodation date range');
      }

      console.log(`Validation completed for accommodation ${accommodationId}:`, {
        valid: validationResult.validLinks.length,
        invalid: validationResult.invalidLinks.length,
        suggestions: validationResult.repairSuggestions.length
      });

      return validationResult;

    } catch (error) {
      console.error('Error validating accommodation day links:', error);
      throw error;
    }
  },

  /**
   * Updates content blocks metadata for an accommodation
   * Tracks which content blocks in day entries reference this accommodation
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation
   * @param {Array} contentBlocks - Array of content block references
   * @returns {Promise<Object>} Updated accommodation with content blocks
   * @throws {Error} If update fails or validation fails
   */
  async updateContentBlocks(userId, tripId, accommodationId, contentBlocks) {
    console.log('🔗 [AccommodationService] updateContentBlocks: Starting content block update', {
      accommodationId,
      contentBlockCount: contentBlocks?.length || 0,
      contentBlockIds: contentBlocks?.map(cb => cb.contentBlockId) || []
    });

    try {
      // Validate content blocks structure
      const validation = this.validateContentBlockReferences(contentBlocks);
      if (!validation.isValid) {
        console.error('❌ [AccommodationService] updateContentBlocks: Validation failed', {
          accommodationId,
          errors: validation.errors
        });
        throw new Error(`Invalid content blocks: ${validation.errors.join(', ')}`);
      }

      // Prepare update data with content blocks and metadata
      const now = new Date().toISOString();
      const updateData = {
        contentBlocks: contentBlocks || [],
        contentBlocksLastUpdated: now,
        updatedAt: now
      };

      console.log('📝 [AccommodationService] updateContentBlocks: Updating accommodation record', {
        accommodationId,
        updateFields: Object.keys(updateData),
        contentBlockCount: contentBlocks?.length || 0
      });

      // Update accommodation record
      await updateAccommodation(userId, tripId, accommodationId, updateData);

      // Fetch updated accommodation
      const updatedAccommodation = await this.getAccommodationById(userId, tripId, accommodationId);

      console.log('✅ [AccommodationService] updateContentBlocks: Content blocks updated successfully', {
        accommodationId,
        contentBlockCount: updatedAccommodation.contentBlocks?.length || 0,
        lastUpdated: updatedAccommodation.contentBlocksLastUpdated
      });

      // Publish event for content block update
      eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_CONTENT_BLOCKS_UPDATED, {
        accommodationId,
        tripId,
        userId,
        contentBlocks: updatedAccommodation.contentBlocks,
        contentBlockCount: updatedAccommodation.contentBlocks?.length || 0,
        updatedAt: now
      });

      return updatedAccommodation;
    } catch (error) {
      console.error('❌ [AccommodationService] updateContentBlocks: Update failed', {
        accommodationId,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  },

  /**
   * Gets content blocks metadata for an accommodation
   * Returns information about which content blocks in day entries reference this accommodation
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation
   * @returns {Promise<Array>} Array of content block references
   * @throws {Error} If retrieval fails
   */
  async getContentBlocks(userId, tripId, accommodationId) {
    console.log('📋 [AccommodationService] getContentBlocks: Retrieving content blocks', {
      accommodationId
    });

    try {
      // Use direct Firebase function to avoid infinite loop (don't call this.getAccommodationById)
      const accommodation = await getAccommodationById(userId, tripId, accommodationId);
      const contentBlocks = accommodation.contentBlocks || [];

      console.log('✅ [AccommodationService] getContentBlocks: Content blocks retrieved', {
        accommodationId,
        contentBlockCount: contentBlocks.length,
        blockTypes: contentBlocks.map(cb => cb.type),
        blockDetails: contentBlocks.map(cb => ({ type: cb.type, id: cb.id || cb.contentBlockId, documentId: cb.documentId })),
        lastUpdated: accommodation.contentBlocksLastUpdated
      });

      return {
        contentBlocks,
        metadata: {
          accommodationId,
          contentBlockCount: contentBlocks.length,
          lastUpdated: accommodation.contentBlocksLastUpdated,
          summary: this._generateContentBlockSummary(contentBlocks)
        }
      };
    } catch (error) {
      console.error('❌ [AccommodationService] getContentBlocks: Retrieval failed', {
        accommodationId,
        error: error.message
      });
      throw error;
    }
  },

  /**
   * Validates content block references structure and data integrity
   * Ensures content block metadata is valid and consistent
   * 
   * Supports multiple content block types:
   * - document_reference: Blocks that link to documents (require documentId, documentTitle)
   * - accommodation-day references: Blocks that track day-accommodation links (require dayId, contentBlockId, date, context)
   * 
   * @param {Array} contentBlocks - Array of content block references to validate
   * @returns {Object} Validation result with isValid boolean and errors array
   */
  validateContentBlockReferences(contentBlocks) {
    console.log('🔍 [AccommodationService] validateContentBlockReferences: Starting validation', {
      hasContentBlocks: !!contentBlocks,
      contentBlockCount: contentBlocks?.length || 0
    });

    const errors = [];

    // Handle null/undefined content blocks
    if (!contentBlocks) {
      console.log('✅ [AccommodationService] validateContentBlockReferences: Empty content blocks (valid)');
      return { isValid: true, errors: [] };
    }

    // Must be an array
    if (!Array.isArray(contentBlocks)) {
      errors.push('Content blocks must be an array');
      return { isValid: false, errors };
    }

    // Track IDs for duplicate detection (by type)
    const dayReferenceIds = [];
    const documentReferenceIds = [];

    // Validate each content block reference based on its type
    contentBlocks.forEach((block, index) => {
      if (!block || typeof block !== 'object') {
        errors.push(`Content block at index ${index} must be an object`);
        return;
      }

      // Determine block type and validate accordingly
      const blockType = block.type;

      if (blockType === 'document_reference') {
        // Validate document_reference blocks
        this._validateDocumentReferenceBlock(block, index, errors, documentReferenceIds);
      } else {
        // Validate accommodation-day reference blocks (default/legacy type)
        this._validateAccommodationDayReferenceBlock(block, index, errors, dayReferenceIds);
      }
    });

    // Check for duplicate IDs within each type
    const uniqueDayIds = new Set(dayReferenceIds);
    if (dayReferenceIds.length !== uniqueDayIds.size) {
      errors.push('Duplicate day reference content block IDs detected');
    }

    const uniqueDocIds = new Set(documentReferenceIds);
    if (documentReferenceIds.length !== uniqueDocIds.size) {
      errors.push('Duplicate document reference IDs detected');
    }

    const result = {
      isValid: errors.length === 0,
      errors
    };

    console.log('✅ [AccommodationService] validateContentBlockReferences: Validation complete', {
      contentBlockCount: contentBlocks.length,
      dayReferenceCount: dayReferenceIds.length,
      documentReferenceCount: documentReferenceIds.length,
      isValid: result.isValid,
      errorCount: errors.length,
      errors: errors
    });

    return result;
  },

  /**
   * Validates a document_reference content block
   * @private
   * @param {Object} block - The content block to validate
   * @param {number} index - Index in the array for error messages
   * @param {Array} errors - Array to push errors to
   * @param {Array} idTracker - Array to track IDs for duplicate detection
   */
  _validateDocumentReferenceBlock(block, index, errors, idTracker) {
    // Required fields for document_reference blocks
    if (!block.documentId) {
      errors.push(`Document reference block at index ${index} missing required field: documentId`);
    } else {
      idTracker.push(block.documentId);
    }

    if (!block.documentTitle && !block.displayText) {
      errors.push(`Document reference block at index ${index} missing required field: documentTitle or displayText`);
    }

    // Field format validation
    if (block.documentId && typeof block.documentId !== 'string') {
      errors.push(`Document reference block at index ${index}: documentId must be a string`);
    }

    if (block.id && typeof block.id !== 'string') {
      errors.push(`Document reference block at index ${index}: id must be a string`);
    }

    if (block.createdAt && !this._isValidISOTimestamp(block.createdAt)) {
      errors.push(`Document reference block at index ${index}: createdAt must be a valid ISO timestamp`);
    }
  },

  /**
   * Validates an accommodation-day reference content block
   * @private
   * @param {Object} block - The content block to validate
   * @param {number} index - Index in the array for error messages
   * @param {Array} errors - Array to push errors to
   * @param {Array} idTracker - Array to track IDs for duplicate detection
   */
  _validateAccommodationDayReferenceBlock(block, index, errors, idTracker) {
    // Required fields for accommodation-day reference blocks
    if (!block.dayId) {
      errors.push(`Content block at index ${index} missing required field: dayId`);
    }

    if (!block.contentBlockId) {
      errors.push(`Content block at index ${index} missing required field: contentBlockId`);
    } else {
      idTracker.push(block.contentBlockId);
    }

    if (!block.date) {
      errors.push(`Content block at index ${index} missing required field: date`);
    }

    if (!block.context) {
      errors.push(`Content block at index ${index} missing required field: context`);
    }

    // Field format validation
    if (block.dayId && typeof block.dayId !== 'string') {
      errors.push(`Content block at index ${index}: dayId must be a string`);
    }

    if (block.contentBlockId && typeof block.contentBlockId !== 'string') {
      errors.push(`Content block at index ${index}: contentBlockId must be a string`);
    }

    if (block.date && !this._isValidISODate(block.date)) {
      errors.push(`Content block at index ${index}: date must be a valid ISO date string`);
    }

    if (block.context) {
      const validContexts = ['check-in', 'staying', 'check-out', 'single-day'];
      if (!validContexts.includes(block.context)) {
        errors.push(`Content block at index ${index}: context must be one of: ${validContexts.join(', ')}`);
      }
    }

    if (block.createdAt && !this._isValidISOTimestamp(block.createdAt)) {
      errors.push(`Content block at index ${index}: createdAt must be a valid ISO timestamp`);
    }
  },

  /**
   * Adds a single content block reference to an accommodation
   * Atomic operation that ensures consistency
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation
   * @param {Object} contentBlockRef - Content block reference to add
   * @returns {Promise<Object>} Updated accommodation
   * @throws {Error} If operation fails
   */
  async addContentBlockReference(userId, tripId, accommodationId, contentBlockRef) {
    console.log('➕ [AccommodationService] addContentBlockReference: Adding content block reference', {
      accommodationId,
      contentBlockId: contentBlockRef.contentBlockId,
      dayId: contentBlockRef.dayId,
      context: contentBlockRef.context
    });

    try {
      // Get current content blocks directly from Firestore to ensure we have the latest data
      // This avoids timing issues where recently written document_reference blocks might be missed
      const accommodation = await getAccommodationById(userId, tripId, accommodationId);
      const currentBlocks = accommodation?.contentBlocks || [];

      // Check for duplicate
      const existingBlock = currentBlocks.find(block =>
        block.contentBlockId === contentBlockRef.contentBlockId
      );

      if (existingBlock) {
        console.warn('⚠️ [AccommodationService] addContentBlockReference: Content block already exists', {
          accommodationId,
          contentBlockId: contentBlockRef.contentBlockId,
          existingDayId: existingBlock.dayId
        });
        throw new Error(`Content block ${contentBlockRef.contentBlockId} already exists in accommodation ${accommodationId}`);
      }

      // Add new content block reference with timestamp
      const newContentBlockRef = {
        ...contentBlockRef,
        createdAt: contentBlockRef.createdAt || new Date().toISOString()
      };

      const updatedBlocks = [...currentBlocks, newContentBlockRef];

      // Update accommodation with new content blocks
      const updatedAccommodation = await this.updateContentBlocks(userId, tripId, accommodationId, updatedBlocks);

      console.log('✅ [AccommodationService] addContentBlockReference: Content block reference added', {
        accommodationId,
        contentBlockId: contentBlockRef.contentBlockId,
        totalContentBlocks: updatedBlocks.length
      });

      return updatedAccommodation;
    } catch (error) {
      console.error('❌ [AccommodationService] addContentBlockReference: Failed to add content block reference', {
        accommodationId,
        contentBlockId: contentBlockRef.contentBlockId,
        error: error.message
      });
      throw error;
    }
  },

  /**
   * Removes a content block reference from an accommodation
   * Atomic operation that ensures consistency
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation
   * @param {string} contentBlockId - ID of content block reference to remove
   * @returns {Promise<Object>} Updated accommodation
   * @throws {Error} If operation fails
   */
  async removeContentBlockReference(userId, tripId, accommodationId, contentBlockId) {
    console.log('➖ [AccommodationService] removeContentBlockReference: Removing content block reference', {
      accommodationId,
      contentBlockId
    });

    try {
      // Get current content blocks directly from Firestore to ensure we have the latest data
      const accommodation = await getAccommodationById(userId, tripId, accommodationId);
      const currentBlocks = accommodation?.contentBlocks || [];

      // Find and remove the content block reference
      const updatedBlocks = currentBlocks.filter(block =>
        block.contentBlockId !== contentBlockId
      );

      if (updatedBlocks.length === currentBlocks.length) {
        console.warn('⚠️ [AccommodationService] removeContentBlockReference: Content block not found', {
          accommodationId,
          contentBlockId,
          availableContentBlockIds: currentBlocks.map(block => block.contentBlockId)
        });
        throw new Error(`Content block ${contentBlockId} not found in accommodation ${accommodationId}`);
      }

      // Update accommodation with filtered content blocks
      const updatedAccommodation = await this.updateContentBlocks(userId, tripId, accommodationId, updatedBlocks);

      console.log('✅ [AccommodationService] removeContentBlockReference: Content block reference removed', {
        accommodationId,
        contentBlockId,
        remainingContentBlocks: updatedBlocks.length
      });

      return updatedAccommodation;
    } catch (error) {
      console.error('❌ [AccommodationService] removeContentBlockReference: Failed to remove content block reference', {
        accommodationId,
        contentBlockId,
        error: error.message
      });
      throw error;
    }
  },

  /**
   * Clears all content block references from an accommodation
   * Used during accommodation deletion or major updates
   * 
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} accommodationId - The ID of the accommodation
   * @returns {Promise<Object>} Updated accommodation
   * @throws {Error} If operation fails
   */
  async clearContentBlockReferences(userId, tripId, accommodationId) {
    console.log('🧹 [AccommodationService] clearContentBlockReferences: Clearing all content block references', {
      accommodationId
    });

    try {
      // Get current content blocks count for logging
      const currentData = await this.getContentBlocks(userId, tripId, accommodationId);
      const currentCount = currentData.contentBlocks?.length || 0;

      // Clear all content blocks
      const updatedAccommodation = await this.updateContentBlocks(userId, tripId, accommodationId, []);

      console.log('✅ [AccommodationService] clearContentBlockReferences: All content block references cleared', {
        accommodationId,
        clearedCount: currentCount
      });

      return updatedAccommodation;
    } catch (error) {
      console.error('❌ [AccommodationService] clearContentBlockReferences: Failed to clear content block references', {
        accommodationId,
        error: error.message
      });
      throw error;
    }
  },

  /**
   * Generates a summary of content blocks for display and logging
   * @private
   * @param {Array} contentBlocks - Array of content block references
   * @returns {Object} Summary information
   */
  _generateContentBlockSummary(contentBlocks) {
    if (!Array.isArray(contentBlocks) || contentBlocks.length === 0) {
      return {
        total: 0,
        byContext: {},
        byDay: {},
        dateRange: null
      };
    }

    const summary = {
      total: contentBlocks.length,
      byContext: {},
      byDay: {},
      dateRange: null
    };

    // Count by context
    contentBlocks.forEach(block => {
      if (block.context) {
        summary.byContext[block.context] = (summary.byContext[block.context] || 0) + 1;
      }
      if (block.dayId) {
        summary.byDay[block.dayId] = (summary.byDay[block.dayId] || 0) + 1;
      }
    });

    // Calculate date range
    const dates = contentBlocks.map(block => block.date).filter(Boolean).sort();
    if (dates.length > 0) {
      summary.dateRange = {
        start: dates[0],
        end: dates[dates.length - 1],
        span: dates.length
      };
    }

    return summary;
  },

  /**
   * Validates ISO date string format
   * @private
   * @param {string} dateString - Date string to validate
   * @returns {boolean} True if valid ISO date
   */
  _isValidISODate(dateString) {
    if (typeof dateString !== 'string') return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime()) && dateString.includes('-');
  },

  /**
   * Validates ISO timestamp string format
   * @private
   * @param {string} timestampString - Timestamp string to validate
   * @returns {boolean} True if valid ISO timestamp
   */
  _isValidISOTimestamp(timestampString) {
    if (typeof timestampString !== 'string') return false;
    const date = new Date(timestampString);
    return !isNaN(date.getTime()) && timestampString.includes('T');
  },

  // =====================================================
  // PRIVATE HELPER METHODS
  // =====================================================

  /**
   * Processes legacy dayLinks format (array of strings) to new enriched format
   * @private
   * @param {Array} dayLinks - Raw dayLinks from accommodation data
   * @returns {Array} Enriched dayLinks with metadata
   */
  _processLegacyDayLinks(dayLinks) {
    if (!Array.isArray(dayLinks) || dayLinks.length === 0) {
      return [];
    }

    // Check if already in new format
    if (typeof dayLinks[0] === 'object' && dayLinks[0].dayId) {
      return dayLinks;
    }

    // Convert legacy format (array of strings) to new format
    return dayLinks.filter(dayId => typeof dayId === 'string').map(dayId => ({
      dayId,
      date: null, // Will need to be populated from day lookup
      context: 'staying', // Default context for legacy links
      linkedAt: new Date().toISOString()
    }));
  },

  /**
   * Enriches day links with metadata and context information
   * @private
   * @param {Array} inputDayLinks - Input dayLinks (legacy or new format)
   * @param {Object} accommodation - Accommodation object for context
   * @returns {Array} Enriched dayLinks with full metadata
   */
  _enrichDayLinks(inputDayLinks, accommodation) {
    if (!Array.isArray(inputDayLinks) || inputDayLinks.length === 0) {
      return [];
    }

    // First, process any legacy format
    const processedLinks = this._processLegacyDayLinks(inputDayLinks);

    // If accommodation has date range, enrich with proper context
    const checkInDate = accommodation.details?.checkInDate;
    const checkOutDate = accommodation.details?.checkOutDate;

    if (!checkInDate) {
      // No dates available, return as-is with default context
      return processedLinks.map(link => ({
        ...link,
        context: link.context || 'staying',
        linkedAt: link.linkedAt || new Date().toISOString()
      }));
    }

    // Enrich with date-based context
    const checkIn = new Date(checkInDate);
    const checkOut = checkOutDate ? new Date(checkOutDate) : null;

    return processedLinks.map(link => {
      let context = 'staying'; // default

      if (link.date) {
        const linkDate = new Date(link.date);

        if (linkDate.toISOString().split('T')[0] === checkInDate) {
          context = 'check-in';
        } else if (checkOut) {
          const dayBeforeCheckOut = new Date(checkOut);
          dayBeforeCheckOut.setDate(dayBeforeCheckOut.getDate() - 1);

          if (linkDate.toISOString().split('T')[0] === dayBeforeCheckOut.toISOString().split('T')[0]) {
            context = 'check-out';
          }
        }
      }

      return {
        dayId: link.dayId,
        date: link.date,
        context: link.context || context,
        linkedAt: link.linkedAt || new Date().toISOString()
      };
    });
  },

  /**
   * Handles document updated events
   * Updates document reference blocks with new metadata
   * 
   * @param {Object} eventData - The document update event data
   */
  async handleDocumentUpdated(eventData) {
    try {
      const { userId, tripId, documentId, updateData } = eventData;
      console.log('📄 [AccommodationService] handleDocumentUpdated: Processing document update', {
        userId,
        tripId,
        documentId,
        hasUpdateData: !!updateData
      });

      if (!userId || !tripId || !documentId) {
        console.warn('⚠️ [AccommodationService] handleDocumentUpdated: Missing required event data');
        return;
      }

      // Get all accommodations for the trip
      const accommodations = await this.fetchAccommodation(userId, tripId);
      if (!accommodations || accommodations.length === 0) {
        console.log('🏨 [AccommodationService] handleDocumentUpdated: No accommodations found for trip');
        return;
      }

      // Process each accommodation to update document references
      for (const accommodation of accommodations) {
        if (!accommodation.contentBlocks || accommodation.contentBlocks.length === 0) continue;

        // Check if this accommodation has document references for the updated document
        const hasDocumentReference = accommodation.contentBlocks.some(
          block => block.type === 'document_reference' && block.documentId === documentId
        );

        if (hasDocumentReference) {
          console.log('🔄 [AccommodationService] handleDocumentUpdated: Updating document references in accommodation', {
            accommodationId: accommodation.id,
            documentId
          });

          // Create updated document object for the utility function
          const updatedDocument = {
            id: documentId,
            ...updateData
          };

          // Update document reference blocks
          const updatedContentBlocks = updateDocumentReferenceBlocks(accommodation.contentBlocks, updatedDocument);

          // Update the accommodation with new content blocks
          await updateAccommodation(userId, tripId, accommodation.id, {
            contentBlocks: updatedContentBlocks
          });

          console.log('✅ [AccommodationService] handleDocumentUpdated: Successfully updated document references in accommodation', {
            accommodationId: accommodation.id,
            documentId
          });
        }
      }
    } catch (error) {
      console.error('❌ [AccommodationService] handleDocumentUpdated: Error updating document references', {
        error: error.message,
        stack: error.stack
      });
    }
  },

  /**
   * Handles document deleted events
   * Removes document reference blocks from accommodations
   * 
   * @param {Object} eventData - The document deletion event data
   */
  async handleDocumentDeleted(eventData) {
    try {
      const { userId, tripId, documentId } = eventData;
      console.log('🗑️ [AccommodationService] handleDocumentDeleted: Processing document deletion', {
        userId,
        tripId,
        documentId
      });

      if (!userId || !tripId || !documentId) {
        console.warn('⚠️ [AccommodationService] handleDocumentDeleted: Missing required event data');
        return;
      }

      // Get all accommodations for the trip
      const accommodations = await this.fetchAccommodation(userId, tripId);
      if (!accommodations || accommodations.length === 0) {
        console.log('🏨 [AccommodationService] handleDocumentDeleted: No accommodations found for trip');
        return;
      }

      // Process each accommodation to remove document references
      for (const accommodation of accommodations) {
        if (!accommodation.contentBlocks || accommodation.contentBlocks.length === 0) continue;

        // Check if this accommodation has document references for the deleted document
        const hasDocumentReference = accommodation.contentBlocks.some(
          block => block.type === 'document_reference' && block.documentId === documentId
        );

        if (hasDocumentReference) {
          console.log('🗑️ [AccommodationService] handleDocumentDeleted: Removing document references from accommodation', {
            accommodationId: accommodation.id,
            documentId
          });

          // Remove document reference blocks
          const updatedContentBlocks = removeDocumentReferenceBlocks(accommodation.contentBlocks, [documentId]);

          // Update the accommodation with new content blocks
          await updateAccommodation(userId, tripId, accommodation.id, {
            contentBlocks: updatedContentBlocks
          });

          console.log('✅ [AccommodationService] handleDocumentDeleted: Successfully removed document references from accommodation', {
            accommodationId: accommodation.id,
            documentId
          });
        }
      }
    } catch (error) {
      console.error('❌ [AccommodationService] handleDocumentDeleted: Error removing document references', {
        error: error.message,
        stack: error.stack
      });
    }
  }
}; 